<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useApiClient } from "@/api/client";
import DocumentAlertListViewDTO = App.Data.Alerts.DocumentAlertListViewDTO;

const { t } = useI18n();

const alertIds = defineModel<number[]>({ default: () => [] });

const availableAlerts = ref<DocumentAlertListViewDTO[]>([]);

const loadAlerts = async () => {
	const result = await useApiClient("alerts").get({
		page: 1,
		itemsPerPage: 1000,
		sortBy: [],
	});
	if (result.isOk()) {
		availableAlerts.value = result.value.data;
	}
};

onMounted(loadAlerts);

const intervalKey = (alert: DocumentAlertListViewDTO): string =>
	JSON.stringify([
		alert.check_interval_type,
		alert.check_interval_specification,
	]);

// All trigger alerts must share the same check interval. Once one is picked,
// alerts on a different interval are disabled.
const selectedIntervalKey = computed<string | null>(() => {
	const first = availableAlerts.value.find((alert) =>
		(alertIds.value ?? []).includes(alert.id ?? -1)
	);
	return first ? intervalKey(first) : null;
});

const alertItems = computed(() =>
	availableAlerts.value.map((alert) => ({
		title: alert.stored_document?.title ?? `Alert #${alert.id}`,
		subtitle: `${t(`properties.${alert.properties.metric_name}.title`)} · ${t(
			`alerts.check_types.${alert.check_interval_type}`
		)}`,
		value: alert.id,
		props: {
			disabled:
				selectedIntervalKey.value !== null &&
				intervalKey(alert) !== selectedIntervalKey.value,
		},
	}))
);
</script>

<template>
	<v-select
		v-model="alertIds"
		:items="alertItems"
		:menu-props="{ class: 'alert-select-menu' }"
		:rules="[
			(v) => (Array.isArray(v) && v.length > 0) || 'Select at least one alert',
		]"
		label="Trigger alerts"
		hint="All selected alerts must share the same check interval"
		persistent-hint
		multiple
		chips
		closable-chips
	>
		<template #item="{ props: itemProps, item }">
			<v-list-item v-bind="itemProps" :subtitle="item.raw.subtitle" />
		</template>
		<template #chip="{ props: itemProps, item }">
			<v-chip
				v-bind="itemProps"
				color="secondary-tonal"
				size="small"
				density="comfortable"
			>
				{{ item.title }}
			</v-chip>
		</template>
	</v-select>
</template>

<style lang="scss">
.alert-select-menu .v-list-item-title {
	font-size: 0.875rem;
}
</style>
